import React, { useCallback } from 'react';
import { useTable, Column } from 'react-table';
import { ILogt } from '../models/logT';
import '../styles/LogTTable.scss';
import LoadingSpinner from './LoadingSpinner';
import SkeletonLoader from './SkeletonLoader';

// Date formatting function
function formatDateToCustom(date: Date): string {
  const pad = (num: number, size: number = 2): string => num.toString().padStart(size, '0');

  const day = pad(date.getDate());
  const month = pad(date.getMonth() + 1); // Months are zero-based
  const year = date.getFullYear();

  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());
  const milliseconds = pad(date.getMilliseconds(), 3); // Pad to 3 digits

  return `${month}-${day}-${year} ${hours}:${minutes}:${seconds}Z`;
}

interface LogTTableProps {
  logTs: any[];
  onRowClick: (row: { original: any | null }) => void;
  selectedMessage: any | null; // Add selectedMessage prop
  onSort: (column: string) => void; // Add onSort prop
  sortColumn: string; // Allow null
  sortDirection: 'ASC' | 'DESC'; // Allow null
  sortLoading: boolean;
  loading: boolean; // Add loading prop for table data
}

// Mapping direction numbers to descriptions
const directionDescriptions: { [key: number]: string } = {
  0: 'In Down - In Downlink',
  1: 'Out Up - Out Uplink',
  2: 'In Up - In Uplink',
  3: 'Out Down - Out Downlink',
  4: 'In Gnd - In Ground',
  5: 'Out Gnd - Out Ground',
  6: 'Re Down - Reprocessed Downlink',
  7: 'Re Up - Reprocessed Uplink',
  8: 'Re Gnd - Reprocessed Ground'
};

const LogTTable: React.FC<LogTTableProps> = ({
  logTs,
  onRowClick,
  selectedMessage,
  onSort,
  sortColumn,
  sortDirection,
  sortLoading,
  loading
}) => {
  const columns: Column<any>[] = React.useMemo(() => [
    { 
      Header: 'DATE & TIME', 
      accessor: 'logtTS' as const,
      Cell: ({ cell: { value } }) => (
        <>{value ? formatDateToCustom(new Date(value)) : ''}</>
      )
    },
    { 
      Header: 'DIRECTION', 
      accessor: 'direction' as const,
      Cell: ({ cell: { value } }) => (
        <>{directionDescriptions[value as number] || value}</>
      )
    },
    { Header: 'MESSAGE TYPE', accessor: 'messageType' as const },
    { Header: 'SMI', accessor: 'smi' as const },
    { Header: 'MEDIUM', accessor: 'medium' as const },
    { Header: 'AIRCRAFT', accessor: 'aircraftId' as const },
    { Header: 'FLIGHT', accessor: 'flightId' as const },
    { Header: 'SOURCE', accessor: 'source' as const },
    { Header: 'ORIG', accessor: 'departureAirportIATA' as const },
    { Header: 'DEST', accessor: 'arrivalAirportIATA' as const },

  ], []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({
    columns,
    data: logTs,
  });

  const handleRowClick = useCallback((row: { original: ILogt | null }) => {
    onRowClick(row);
  }, [onRowClick]);

  const handleHeaderClick = useCallback((columnId: string) => {
    onSort(columnId);
  }, [onSort, sortLoading]);

  return (
    <div className="table-container">
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <th
                  {...column.getHeaderProps()}
                  onClick={() => handleHeaderClick(column.id)}
                  className={sortColumn === column.id ? (sortDirection === 'ASC' ? 'sorted-asc' : 'sorted-desc') : ''}
                >
                  <div className="header-spinner-container">
                    {column.render('Header')}
                    {sortColumn === column.id ? (sortDirection === 'ASC' ? ' 🔼' : ' 🔽') : ''}
                    {sortColumn === column.id && sortLoading && (
                      <div className="header-spinner">
                        <LoadingSpinner size="small" />
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {loading ? (
            <SkeletonLoader rows={10} columns={columns.length} />
          ) : (
            rows.map(row => {
              prepareRow(row);
              return (
                <tr
                  {...row.getRowProps()}
                  onClick={() => handleRowClick(row)}
                  className={selectedMessage && selectedMessage.logtTS === row.original?.logtTS ? 'selected-message' : ''}
                >
                  {row.cells.map(cell => (
                    <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default LogTTable;
